import React, {useMemo, useState} from 'react';

import {ResponsiveTreeMap} from '@nivo/treemap';
import {useStore} from 'effector-react';
import {lighten} from 'polished';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {Spinner} from 'components/ui-kit/Spinner';
import {dashboardState$} from 'models/dashboard';
import {colors} from 'theme';

import {ProductTopRecord} from 'types';
import {formatNumber} from 'utils/number';

import {BlockTitle, BlockTitleDescription, SpinnerLayer} from '../styled';

import {Root} from './styled';

type Props = {
    data: ProductTopRecord[];
    height?: number | string;
};

const CustomRoot = styled(Root)`
    display: flex;
    flex-direction: column;
    height: 350px;
`;

const TooltipContainer = styled.div`
    padding: 8px;
    background: ${colors.white};
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    font-size: 13px;
`;

const EmptyContainer = styled.div`
    display: grid;
    place-content: center;
    height: 100%;
`;

const LegendContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;
    gap: 12px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: default;

    & > div {
        border-radius: 4px;
        width: 16px;
        aspect-ratio: 1;
    }

    & > span {
        color: #303030;
        font-size: 12px;
    }
`;

export const ProductTopSquareChart = ({data}: Props) => {
    const {t} = useTranslation();
    const {productTopPending} = useStore(dashboardState$);

    const [hovered, setHovered] = useState<string | null>(null);

    const colorMap: Record<number, string> = {
        0: '#9F6EC4',
        1: '#667EEA',
        2: '#F093FB',
        3: '#CAFCFF',
        4: '#4FACFE',
    };

    const chartData = useMemo(() => {
        const top5 = [...data].sort((a, b) => b.value - a.value).slice(0, 5);
        return {
            name: 'root',
            colors: colors.white,
            children: top5.map((item, id) => ({
                name: item.name,
                value: item.value,
                color: colorMap[id] ?? colorMap[0],
            })),
        };
    }, [data]);

    const leaves = useMemo(() => chartData.children || [], [chartData]);

    return (
        <CustomRoot>
            {productTopPending ? (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            ) : (
                <div>
                    <BlockTitle>{t('pages.dashboard.topChart')}</BlockTitle>
                    <BlockTitleDescription>
                        {t('pages.dashboard.topChartDesc')}
                    </BlockTitleDescription>
                    <div style={{height: 250}}>
                        {chartData.children.some((c) => c.value) ? (
                            <ResponsiveTreeMap<any>
                                data={chartData}
                                identity="name"
                                value="value"
                                leavesOnly={true}
                                innerPadding={4}
                                labelSkipSize={50}
                                label={(node) => node.data.name}
                                colors={(node) =>
                                    hovered && hovered !== node.data.name
                                        ? lighten(0.2, node.data.color)
                                        : node.data.color
                                }
                                borderColor="#fff"
                                nodeOpacity={1}
                                labelTextColor={{from: 'color', modifiers: [['darker', 2]]}}
                                tooltip={({node}) => (
                                    <TooltipContainer>
                                        <span>{node.data.name}</span>:{' '}
                                        <strong>{formatNumber(node.data?.value ?? 0)}</strong>
                                    </TooltipContainer>
                                )}
                            />
                        ) : (
                            <EmptyContainer>No sales</EmptyContainer>
                        )}
                    </div>
                </div>
            )}

            <LegendContainer>
                {leaves.map((node, i) => (
                    <LegendItem
                        key={i}
                        onMouseEnter={() => setHovered(node.name)}
                        onMouseLeave={() => setHovered(null)}
                    >
                        <div style={{backgroundColor: node.color}} />
                        <span>{node.name}</span>
                    </LegendItem>
                ))}
            </LegendContainer>
        </CustomRoot>
    );
};
